import React, { useState, useEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Container, Row, Col, Form, Button, Spinner, Modal
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faCircleNotch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import BusinessRegisterController from '../Controller/BusinessRegisterController';
import Utility from '../../../../GlobalModules/Utility/Utility';
import '../Assets/css/business-register.scss';

const RegisterOpen = ({ t, i18n }) => {
  const history = useHistory();
  const lng = i18n.language;

  // State variables
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [billingCounters, setBillingCounters] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [serverErrors, setServerErrors] = useState('');
  const [errorClass, setErrorClass] = useState('');
  const [currentOccupants, setCurrentOccupants] = useState([]);
  const [selectedCounterWithOpenRegister, setSelectedCounterWithOpenRegister] = useState(null);

  const [formData, setFormData] = useState({
    billing_counter_slack: '',
    opening_amount: 0
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch billing counters on component mount
  useEffect(() => {
    const fetchBillingCounters = async () => {
      setLoading(true);
      try {
        // Get store ID from local storage
        const storeId = localStorage.getItem('storeId') || localStorage.getItem('store_id');

        // Call the API to fetch billing counters
        const counters = await BusinessRegisterController.getBillingCounters(storeId);

        if (counters && counters.length > 0) {
          setBillingCounters(counters);
        } else {
          setBillingCounters([]);
          console.info('No billing counters found for this store');
        }
      } catch (error) {
        console.error('Error fetching billing counters:', error);
        Utility.toastNotifications(t('registerOpen.errorFetchingCounters'), 'error');
        setBillingCounters([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingCounters();
  }, [t]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for the field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }

    // Clear any server errors when user makes changes
    if (serverErrors) {
      setServerErrors('');
      setErrorClass('');
    }
  };

  // Handle counter selection
  const handleCounterSelect = (slack, index) => {
    // Update form data
    setFormData({
      ...formData,
      billing_counter_slack: slack
    });

    // Get counter data
    const counter = billingCounters[index];
    if (counter) {
      // Check if counter has an opened register
      if (counter.currentOpenedRegisterData && Object.keys(counter.currentOpenedRegisterData).length > 0) {
        setSelectedCounterWithOpenRegister(counter.currentOpenedRegisterData);
        setCurrentOccupants([]); // Clear occupants for opened registers
        setFormData(prevState => ({
          ...prevState,
          opening_amount: 0
        }));
      } else {
        setSelectedCounterWithOpenRegister(null);
        setCurrentOccupants(counter.occupant_data || []);

        // If the counter has occupants, set opening amount to 0 as in legacy
        if (counter.occupant_data && counter.occupant_data.length > 0) {
          setFormData(prevState => ({
            ...prevState,
            opening_amount: 0
          }));
        }
      }
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.billing_counter_slack) {
      errors.billing_counter_slack = t('registerOpen.validation.selectCounter');
    }

    if (formData.opening_amount < 0) {
      errors.opening_amount = t('registerOpen.validation.negativeAmount');
    }

    // Only require opening amount if the field is not disabled (no occupants and no opened register)
    if (currentOccupants.length === 0 && !selectedCounterWithOpenRegister) {
      if (formData.opening_amount === '' || formData.opening_amount === null || formData.opening_amount === undefined) {
        errors.opening_amount = 'Opening amount is required';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form confirmation
  const handleFormConfirm = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show confirmation modal as in legacy
    setShowConfirmModal(true);
  };

  // Handle actual form submission after confirmation
  const handleSubmit = async () => {
    setProcessing(true);
    setShowConfirmModal(false);

    try {
      // Add user and store IDs to the request
      const registerData = {
        billing_counter: formData.billing_counter_slack, // Map to expected API parameter
        user_id: parseInt(localStorage.getItem('userId') || localStorage.getItem('user_id')),
        store_id: parseInt(localStorage.getItem('storeId') || localStorage.getItem('store_id')),
        opening_amount: parseFloat(formData.opening_amount)
      };

      const response = await BusinessRegisterController.openBusinessRegister(registerData);

      if (response) {
        Utility.toastNotifications(t('registerOpen.openSuccess'), 'success');
        // Redirect back to the POS screen
        history.push(`/${lng}/sales/orders/add`);
      }
    } catch (error) {
      console.error('Error opening business register:', error);
      setServerErrors(error.message || t('registerOpen.openError'));
      setErrorClass('text-danger');
      Utility.toastNotifications(t('registerOpen.openError'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Handle cancel/back button
  const handleBack = () => {
    history.goBack();
  };

  return (
    <Container className="my-4 registerOpenPage">
      <Row>
        <Col md={12}>
          <Form onSubmit={handleFormConfirm}>
            {/* Header with title and button - matching legacy layout */}
            <div className="d-flex flex-wrap mb-4 registerOpenPageAlignCenter">
              <div className="mr-auto">
                <span className="h4">{t('registerOpen.openRegister')}</span>
              </div>
              {billingCounters.length > 0 && (
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={processing}
                  >
                    {processing && <FontAwesomeIcon icon={faCircleNotch} spin className="me-2" />}
                    {selectedCounterWithOpenRegister ?
                      t('registerOpen.joinRegisterAndContinue') :
                      currentOccupants.length === 0 ?
                        t('registerOpen.openRegisterAndContinue') :
                        t('registerOpen.joinRegisterAndContinue')}
                  </Button>
                </div>
              )}
            </div>

            {/* Server error messages */}
            {serverErrors && (
              <p className={errorClass}>{serverErrors}</p>
            )}

            {billingCounters.length > 0 ? (
              <>
                {/* Billing Counter Selection - Using radio buttons like legacy */}
                <Form.Group className="mb-3">
                  <Form.Label className='chooseBillingCounter'>{t('registerOpen.chooseBillingCounter')}</Form.Label>
                  <div className="d-flex flex-wrap">
                    <Row className="g-3 openRegisterPanel">
                      {billingCounters.map((counter, index) => {
                        const isSelected = formData.billing_counter_slack === counter.slack;
                        const hasOpenedRegister =
                          counter.currentOpenedRegisterData &&
                          Object.keys(counter.currentOpenedRegisterData).length > 0;
                        const hasOccupants =
                          counter.occupant_data && counter.occupant_data.length > 0;

                        // Dynamic column sizing based on number of counters
                        const getColumnClass = () => {
                          if (billingCounters.length === 1) {
                            return '12'; // Full width for single counter
                          } else if (billingCounters.length === 2) {
                            return '6'; // Half width for two counters
                          } else {
                            return '4'; // Third width for 3+ counters (original)
                          }
                        };

                        return (
                          <Col xl={getColumnClass()} lg={getColumnClass()} md={getColumnClass()} sm={12} key={counter.slack}>
                            <div className="counter-card-wrapper">
                              <Form.Check
                                type="radio"
                                id={`billing_counter${index}`}
                                name="billing_counter_slack"
                                value={counter.slack}
                                checked={isSelected}
                                onChange={() => {
                                  handleCounterSelect(counter.slack, index);
                                  setFormErrors({ ...formErrors, billing_counter_slack: '' });
                                }}
                                className="counter-check"
                                label={
                                  <div className={`counter-card ${isSelected ? 'selected' : ''}`}>
                                    <div className="counter-card-header">
                                      <div className="counter-title-wrap">
                                        <h6 className="counter-title mb-1">
                                          {counter.billing_counter_code} - {counter.counter_name}
                                        </h6>

                                        {!hasOpenedRegister ? (
                                          hasOccupants ? (
                                            <span className="counter-badge badge-occupied">
                                              {counter.occupant_data.length} {t('registerOpen.occupants')}
                                            </span>
                                          ) : (
                                            <span className="counter-badge badge-free">
                                              {t('registerOpen.free')}
                                            </span>
                                          )
                                        ) : (
                                          <span className="counter-badge badge-open">
                                            Active Register
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="counter-card-body">
                                      {hasOpenedRegister ? (
                                        <div className="register-info-panel">
                                          <div className="register-info-title">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            <span>
                                              This register is already open. Join the active register to continue.
                                            </span>
                                          </div>

                                          <div className="register-info-details">
                                            <div className="register-info-row">
                                              <span className="label">Opened by</span>
                                              <span className="value">
                                                {counter.currentOpenedRegisterData.openedBy}
                                              </span>
                                            </div>
                                            <div className="register-info-row">
                                              <span className="label">Opened at</span>
                                              <span className="value">
                                                {counter.currentOpenedRegisterData.opening_date}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="counter-empty-state">
                                          {hasOccupants ? (
                                            <span>This counter currently has active occupants.</span>
                                          ) : (
                                            <span>This counter is available for use.</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                }
                              />
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                  {formErrors.billing_counter_slack && (
                    <div className="text-danger">{formErrors.billing_counter_slack}</div>
                  )}
                </Form.Group>

                {/* Opening Amount */}
                {!selectedCounterWithOpenRegister && (
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>{t('registerOpen.cashInHand')}</Form.Label>
                        <Form.Control
                          type="number"
                          name="opening_amount"
                          value={formData.opening_amount}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          placeholder={t('registerOpen.enterCashInHand')}
                          disabled={currentOccupants.length > 0}
                        />
                        {formErrors.opening_amount && (
                          <div className="text-danger">{formErrors.opening_amount}</div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <p>{t('registerOpen.allCountersOccupied')}</p>
            )}
          </Form>

          {/* Confirmation Modal - Matching legacy behavior */}
          <Modal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>{t('common.confirm')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {t('registerOpen.confirmOpenRegister')}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                {t('common.buttons.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                {t('common.buttons.submit')}
              </Button>
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default withNamespaces()(RegisterOpen);
